import React, { useState, useEffect, useCallback, useRef } from 'react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { toast } from 'react-toastify';
import CcmLicenseBanner from '../components/CcmLicenseBanner';
import {
  Users, UserPlus, UserMinus, Search, ChevronRight, ChevronLeft,
  X, Check, Loader2, AlertTriangle, RefreshCw, Upload, Download, Camera,
  Shield, Trash2, RotateCcw,
  CheckCircle, XCircle, Mail, Phone,
  Plus, FolderOpen, Activity, Video, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import registrationTutorial from '../components/tutorials/configs/registrationTutorial';

const STATUS_COLORS = { ACTIVE: '#22c55e', PENDING: '#f59e0b', SUSPENDED: '#ef4444', DEREGISTERED: '#64748b' };
const SYNC_COLORS = { SYNCED: '#22c55e', PENDING: '#f59e0b', FAILED: '#ef4444', RETRY: '#f59e0b', NOT_APPLICABLE: '#64748b' };
const PROVIDER_LABELS = { FR_OOSTO: 'Oosto FR', AC_FEENICS: 'Feenics AC', AC_LENEL: 'Lenel AC', AC_3XLOGIC: '3xLogic AC', AC_CCURE: 'C-CURE AC', AC_GENETEC: 'Genetec AC', AC_MERCURY: 'Mercury AC', ENTRY_TABLET: 'Entry' };
const ACTION_LABELS = { REGISTER: 'Registered', DEREGISTER: 'Deregistered', UPDATE: 'Updated', SYNC: 'Synced', RETRY: 'Retry', ASSIGN_GROUP: 'Group Assigned', REMOVE_GROUP: 'Group Removed', ENROLL_FACE: 'Face Enrolled', REMOVE_FACE: 'Face Removed', BULK_IMPORT: 'Bulk Import' };

// Convert Oosto internal URL to proxy URL. Phase 3, Task C: routed
// through the registration module's subject-image proxy, which resolves
// the tenant's own OnWatch connection instead of a hardcoded appliance.
function oostoProxyUrl(url) {
  if (!url) return null;
  const tenantId = localStorage.getItem('currentTenantId') || '';
  try {
    const parsed = new URL(url);
    return '/api/registration/subject-image?path=' + encodeURIComponent(parsed.pathname) + '&tenantId=' + tenantId;
  } catch(e) {
    return "/api/registration/subject-image?path=" + encodeURIComponent(url) + "&tenantId=" + tenantId;
  }
}

const syncDot = (st) => ({ width: '8px', height: '8px', borderRadius: '50%', background: SYNC_COLORS[st] || 'var(--text-tertiary)', display: 'inline-block', marginRight: '4px' });
const statusBadge = (st) => ({ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: `${STATUS_COLORS[st] || 'var(--text-tertiary)'}20`, color: STATUS_COLORS[st] || 'var(--text-tertiary)' });

export default function RegistrationNew() {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(registrationTutorial);

  const [activeTab, setActiveTab] = useState('register');
  const [persons, setPersons] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const searchTimeout = useRef(null);

  const fetchPersons = useCallback(async () => { setLoading(true); try { const p = new URLSearchParams({ page, limit: 50 }); if (search) p.set('search', search); if (statusFilter) p.set('status', statusFilter); const res = await fetchWithTenant(`/api/registration/persons?${p}`); const d = await res.json(); setPersons(d.persons||[]); setTotal(d.total||0); } catch(e){console.error(e);} setLoading(false); }, [page, search, statusFilter]);
  const fetchGroups = useCallback(async () => { try { const r = await fetchWithTenant('/api/registration/groups'); const d = await r.json(); setGroups(d.groups||[]); } catch(e){} }, []);
  const fetchProviders = useCallback(async () => { try { const r = await fetchWithTenant('/api/registration/providers'); const d = await r.json(); setProviders(d.providers||[]); } catch(e){} }, []);

  useEffect(() => { fetchGroups(); fetchProviders(); }, []);
  useEffect(() => { if (activeTab === 'people') fetchPersons(); }, [fetchPersons, activeTab]);

  const handleSearch = (v) => { clearTimeout(searchTimeout.current); searchTimeout.current = setTimeout(() => { setSearch(v); setPage(1); }, 300); };
  const handleExport = async () => { try { const r = await fetchWithTenant('/api/registration/export'); const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href=u; a.download=`registration-export-${new Date().toISOString().split('T')[0]}.csv`; a.click(); URL.revokeObjectURL(u); toast.success('Exported'); } catch(e){ toast.error('Export failed'); } };

  return (
    <div className="reg-page">
      <CcmLicenseBanner />
      <div className="reg-header">
        <h1 className="reg-title"><Users size={28} className="reg-icon-accent" /> Registration</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="tutorial-help-btn" onClick={() => startTutorial(registrationTutorial.module, registrationTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          <button className="reg-btn" onClick={handleExport}><Download size={14} /> Export CSV</button>
        </div>
      </div>
      <div className="reg-tabs">
        {[{id:'register',label:'Register',icon:UserPlus},{id:'unregister',label:'Unregister',icon:UserMinus},{id:'people',label:'People',icon:Users},{id:'groups',label:'Groups',icon:FolderOpen},{id:'audit',label:'Audit Log',icon:Activity}].map(t => (
          <button key={t.id} className={`reg-tab ${(activeTab===t.id) ? 'active' : ''}`} onClick={()=>setActiveTab(t.id)}><t.icon size={15}/> {t.label}</button>
        ))}
      </div>
      {activeTab==='register' && <RegisterTab groups={groups} providers={providers} onComplete={()=>{setActiveTab('people');fetchPersons();}} />}
      {activeTab==='unregister' && <UnregisterTab />}
      {activeTab==='people' && <PeopleTab />}
      {activeTab==='groups' && <GroupsTab />}
      {activeTab==='audit' && <AuditTab />}
      {selectedPerson && <PersonDetail personId={selectedPerson.id} onClose={()=>setSelectedPerson(null)} onUpdate={fetchPersons} />}
      {showPrompt && (
        <TutorialPrompt
          module={registrationTutorial.module}
          moduleName={registrationTutorial.moduleName}
          steps={registrationTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
}

// ─── REGISTER TAB ─────────────────────────────────────────────

function RegisterTab({ groups, providers, onComplete }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', accessLevel: '' });
  const [accessLevels, setAccessLevels] = useState([]);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [photo, setPhoto] = useState(null);
  const [faceData, setFaceData] = useState(null);
  const [faceStatus, setFaceStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Whether any configured provider supports face enrollment.
  // When false, the photo field becomes optional and no /extract-face call is made.
  const hasFR = Array.isArray(providers) && providers.some(p => p && p.supportsFaceEnrollment);

  useEffect(() => { (async () => { try { const r = await fetchWithTenant('/api/registration/access-levels'); const d = await r.json(); setAccessLevels(d.accessLevels||[]); } catch(e){} setLoadingLevels(false); })(); }, []);

  const handlePhotoCapture = async (dataUrl) => {
    setPhoto(dataUrl); setFaceData(null);
    // No FR provider — photo is saved as optional headshot, no face extraction
    if (!hasFR) {
      setFaceStatus('skipped');
      return;
    }
    setFaceStatus('extracting');
    try {
      const base64 = dataUrl.split(',')[1]; const bin = atob(base64);
      const bytes = new Uint8Array(bin.length); for (let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'image/jpeg' });
      const fd = new FormData(); fd.append('photo', blob, 'capture.jpg');
      const res = await fetchWithTenant('/api/registration/extract-face', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success && data.faceDetected) { setFaceData(data.faceData); setFaceStatus('success'); }
      else { setFaceStatus('failed'); toast.error('No face detected. Try a clearer photo.'); }
    } catch(e) { setFaceStatus('failed'); toast.error('Face extraction failed'); }
  };

  const handleSubmit = async () => {
    if (!form.firstName.trim()||!form.lastName.trim()) { toast.error('Name required'); return; }
    if (hasFR && faceStatus !== 'success') { toast.error('Capture a photo with a detectable face first'); return; }
    setSubmitting(true);
    try {
      const res = await fetchWithTenant('/api/registration/persons', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ person: form, groupIds: selectedGroups, faceData }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      const data = await res.json();
      if (data.person?.id && photo) {
        const base64 = photo.split(',')[1]; const bin = atob(base64);
        const bytes = new Uint8Array(bin.length); for(let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
        const blob = new Blob([bytes],{type:'image/jpeg'}); const fd = new FormData(); fd.append('photo',blob,'enrollment.jpg');
        await fetchWithTenant(`/api/registration/persons/${data.person.id}/photo`, { method: 'POST', body: fd });
      }
      const card = data.cardNumber || data.syncResults?.find(r=>r.cardNumber)?.cardNumber;
      const syncs = data.syncResults || [];
      const failures = syncs.filter(r => !r.success);
      const name = `${form.firstName} ${form.lastName}`;
      if (syncs.length > 0 && failures.length === syncs.length) {
        toast.error(`${name} was SAVED but NOT synced to any system (${failures.map(f=>f.integrationName).join(', ')}). They do NOT have access yet - use Retry on their profile.`, { autoClose: false });
      } else if (failures.length > 0) {
        toast.warn(`${name} partially registered. FAILED: ${failures.map(f=>f.integrationName).join(', ')}. Open their profile to Retry.`, { autoClose: false });
      } else {
        toast.success(`${name} registered${card ? ` (Card #${card})` : ''}`);
      }
      setForm({ firstName:'', lastName:'', accessLevel:'' }); setPhoto(null); setFaceData(null); setFaceStatus(''); setSelectedGroups([]);
      onComplete();
    } catch(e) { toast.error(e.message); }
    setSubmitting(false);
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div className="reg-card"><div style={{ padding: '24px' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><UserPlus size={18} className="reg-icon-accent"/> Register New Person</h3>
        <div className="reg-form-row">
          <div className="reg-form-group"><label className="reg-form-label">First Name *</label><input className="reg-form-input" value={form.firstName} onChange={e=>setForm(p=>({...p,firstName:e.target.value}))} placeholder="First name" autoFocus /></div>
          <div className="reg-form-group"><label className="reg-form-label">Last Name *</label><input className="reg-form-input" value={form.lastName} onChange={e=>setForm(p=>({...p,lastName:e.target.value}))} placeholder="Last name" /></div>
        </div>
        <div className="reg-form-group">
          <label className="reg-form-label"><Shield size={12} style={{marginRight:'4px'}}/> Level of Access</label>
          {loadingLevels ? <div className="reg-form-input" style={{display:'flex',alignItems:'center',gap:'8px',color:'var(--text-tertiary)'}}><Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/> Loading...</div>
           : accessLevels.length > 0 ? <select className="reg-form-input" style={{cursor:'pointer'}} value={form.accessLevel} onChange={e=>setForm(p=>({...p,accessLevel:e.target.value}))}><option value="">Select...</option>{accessLevels.map(al=><option key={al.id||al.href} value={al.name}>{al.name}</option>)}</select>
           : <input className="reg-form-input" value={form.accessLevel} onChange={e=>setForm(p=>({...p,accessLevel:e.target.value}))} placeholder="e.g. Full Access" />}
        </div>
        {groups.length > 0 && <div className="reg-form-group"><label className="reg-form-label">Groups</label><div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>{groups.map(g=>(
          <label key={g.id} style={{display:'flex',alignItems:'center',gap:'6px',padding:'6px 12px',background:selectedGroups.includes(g.id)?'rgba(0,212,170,0.1)':'rgba(255,255,255,0.04)',border:`1px solid ${selectedGroups.includes(g.id)?'rgba(0,212,170,0.3)':'rgba(255,255,255,0.08)'}`,borderRadius:'20px',cursor:'pointer',fontSize:'13px'}}>
            <input type="checkbox" style={{display:'none'}} checked={selectedGroups.includes(g.id)} onChange={e=>{if(e.target.checked)setSelectedGroups(p=>[...p,g.id]);else setSelectedGroups(p=>p.filter(id=>id!==g.id));}} />
            {g.color && <span style={{width:'8px',height:'8px',borderRadius:'50%',background:g.color}}/>}{g.name}
          </label>))}</div></div>}
        <div className="reg-form-group"><label className="reg-form-label"><Camera size={12} style={{marginRight:'4px'}}/> {hasFR ? 'Enrollment Photo *' : 'Headshot (optional)'}</label><PhotoCapture photo={photo} onCapture={handlePhotoCapture} onClear={()=>{setPhoto(null);setFaceData(null);setFaceStatus('');}} faceStatus={faceStatus} hasFR={hasFR} /></div>
        <button className="reg-btn reg-btn-primary" style={{width:'100%',justifyContent:'center',padding:'12px',fontSize:'15px',marginTop:'8px'}} onClick={handleSubmit} disabled={submitting||!form.firstName.trim()||!form.lastName.trim()||(hasFR&&faceStatus!=='success')}>
          {submitting?<><Loader2 size={16} style={{animation:'spin 1s linear infinite'}}/> Registering...</>:<><UserPlus size={16}/> Register Person</>}
        </button>
      </div></div>
    </div>
  );
}

// ─── PHOTO CAPTURE ────────────────────────────────────────────

function PhotoCapture({ photo, onCapture, onClear, faceStatus, hasFR }) {
  const videoRef = useRef(null); const canvasRef = useRef(null); const fileRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [camErr, setCamErr] = useState(null);
  const streamRef = useRef(null);
  const pendingStream = useRef(null);

  // When streaming turns on and video element mounts, attach the stream
  useEffect(() => {
    if (streaming && videoRef.current && pendingStream.current) {
      videoRef.current.srcObject = pendingStream.current;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play().then(() => setVideoReady(true)).catch(() => setVideoReady(true));
      };
      pendingStream.current = null;
    }
  }, [streaming]);

  const startCam = async () => {
    try {
      setCamErr(null); setVideoReady(false);
      const ms = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = ms;
      pendingStream.current = ms;
      setStreaming(true); // This triggers re-render with video element, then useEffect attaches stream
    } catch(e) { setCamErr('Camera unavailable. Upload a photo instead.'); }
  };

  const stopCam = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    pendingStream.current = null; setStreaming(false); setVideoReady(false);
  };

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const c = canvasRef.current, v = videoRef.current;
    c.width = v.videoWidth || 640; c.height = v.videoHeight || 480;
    c.getContext('2d').drawImage(v, 0, 0);
    const dataUrl = c.toDataURL('image/jpeg', 0.9);
    stopCam(); onCapture(dataUrl);
  };

  const handleFile = (e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = (ev) => { stopCam(); onCapture(ev.target.result); }; r.readAsDataURL(f); };
  useEffect(() => () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); }, []);

  if (photo) return (
    <div style={{display:'flex',gap:'16px',alignItems:'flex-start'}}>
      <div style={{position:'relative',flexShrink:0}}>
        <img src={photo} alt="Captured" style={{width:'160px',height:'160px',borderRadius:'10px',objectFit:'cover',border:`2px solid ${faceStatus==='success'?'#22c55e':faceStatus==='failed'?'#ef4444':faceStatus==='skipped'?'#0ea5e9':'#f59e0b'}`}} />
        {faceStatus==='extracting'&&<div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.5)',borderRadius:'10px',display:'flex',alignItems:'center',justifyContent:'center'}}><Loader2 size={28} style={{color:'#f59e0b',animation:'spin 1s linear infinite'}}/></div>}
      </div>
      <div style={{flex:1}}>
        {faceStatus==='extracting'&&<p style={{color:'#f59e0b',margin:'0 0 8px',fontSize:'13px'}}><Loader2 size={12} style={{animation:'spin 1s linear infinite',marginRight:'6px'}}/>Detecting face...</p>}
        {faceStatus==='success'&&<p style={{color:'#22c55e',margin:'0 0 8px',fontSize:'13px'}}><CheckCircle size={12} style={{marginRight:'4px'}}/> Face detected</p>}
        {faceStatus==='failed'&&<p style={{color:'#ef4444',margin:'0 0 8px',fontSize:'13px'}}><XCircle size={12} style={{marginRight:'4px'}}/> No face detected</p>}
        {faceStatus==='skipped'&&<p style={{color:'#0ea5e9',margin:'0 0 8px',fontSize:'13px'}}><CheckCircle size={12} style={{marginRight:'4px'}}/> Photo added (no FR enrollment)</p>}
        <div style={{display:'flex',gap:'8px'}}><button className="reg-btn reg-btn-sm" onClick={onClear}><X size={12}/> Retake</button><button className="reg-btn reg-btn-ghost reg-btn-sm" onClick={()=>fileRef.current?.click()}><Upload size={12}/> Upload</button></div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{display:'none'}} onChange={handleFile} />
      </div>
    </div>
  );

  if (streaming) return (
    <div><canvas ref={canvasRef} style={{display:'none'}} />
      <div style={{position:'relative',borderRadius:'10px',overflow:'hidden',background:'#000',maxWidth:'400px'}}>
        <video ref={videoRef} autoPlay playsInline muted style={{width:'100%',display:'block',transform:'scaleX(-1)'}} />
        <div style={{position:'absolute',inset:0,border:'2px solid rgba(0,212,170,0.4)',borderRadius:'10px',pointerEvents:'none'}} />
      </div>
      <div style={{display:'flex',gap:'8px',marginTop:'12px'}}>
        <button className="reg-btn reg-btn-primary" onClick={capture} disabled={!videoReady}><Camera size={14}/> {videoReady ? 'Capture' : 'Starting...'}</button>
        <button className="reg-btn reg-btn-ghost" onClick={stopCam}><X size={14}/> Cancel</button>
      </div>
    </div>
  );

  return (
    <div>
      <canvas ref={canvasRef} style={{display:'none'}} />
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{display:'none'}} onChange={handleFile} />
      <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
        <button className="reg-btn" onClick={startCam}><Video size={14}/> Use Camera</button>
        <button className="reg-btn" onClick={()=>fileRef.current?.click()}><Upload size={14}/> Upload Photo</button>
      </div>
      {camErr&&<p style={{color:'#f59e0b',fontSize:'12px',margin:'4px 0 0'}}>{camErr}</p>}
    </div>
  );
}

// ─── UNREGISTER TAB ───────────────────────────────────────────

function UnregisterTab() {
  const [subjects, setSubjects] = useState([]); const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(''); const [selected, setSelected] = useState(new Set()); const [deleting, setDeleting] = useState(false);

  const fetch_ = async () => { setLoading(true); try { const r = await fetchWithTenant('/api/registration/oosto-subjects'); const d = await r.json(); setSubjects(d.persons||[]); } catch(e){toast.error('Failed to load');} setLoading(false); };
  useEffect(()=>{fetch_();},[]);

  const filtered = subjects.filter(p=>!searchTerm||(p.name||'').toLowerCase().includes(searchTerm.toLowerCase()));
  const toggle = (id) => setSelected(prev=>{const n=new Set(prev);if(n.has(id))n.delete(id);else n.add(id);return n;});

  const handleUnreg = async () => {
    if(selected.size===0)return;
    const names=[...selected].map(id=>subjects.find(s=>s.externalId===id)?.name||'Unknown');
    if(!confirm(`Remove ${selected.size} person(s) from FR and access control?\n\n${names.join('\n')}`))return;
    setDeleting(true); let ok=0;
    for(const id of selected){
      const sub=subjects.find(s=>s.externalId===id);
      try{const r=await fetchWithTenant('/api/registration/deregister-by-oosto',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({oostoSubjectId:id,personName:sub?.name})}); const d=await r.json(); if(d.success)ok++; else toast.error(`Failed: ${sub?.name}`);}catch(e){toast.error(`Error: ${sub?.name}`);}
    }
    if(ok>0)toast.success(`Removed ${ok} person(s)`);
    setSelected(new Set()); fetch_(); setDeleting(false);
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px',flexWrap:'wrap',gap:'10px'}}>
        <div style={{position:'relative',flex:'1 1 250px',maxWidth:'400px'}}>
          <Search size={16} style={{position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',color:'var(--text-tertiary)'}} />
          <input className="reg-search-input" placeholder="Search by name..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} />
        </div>
        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
          {selected.size>0&&<span style={{fontSize:'13px',color:'#f59e0b'}}>{selected.size} selected</span>}
          <button className="reg-btn reg-btn-ghost reg-btn-sm" onClick={fetch_}><RefreshCw size={14}/></button>
          <button className="reg-btn reg-btn-danger" onClick={handleUnreg} disabled={selected.size===0||deleting}>
            {deleting?<><Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/> Removing...</>:<><UserMinus size={14}/> Unregister ({selected.size})</>}
          </button>
        </div>
      </div>
      {loading?<div className="reg-empty"><Loader2 size={28} style={{animation:'spin 1s linear infinite'}}/></div>
       :filtered.length===0?<div className="reg-card reg-empty"><Users size={40} style={{marginBottom:'12px',opacity:0.3}}/><p style={{margin:0}}>{searchTerm?'No match':'No subjects in FR'}</p></div>
       :<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))',gap:'12px'}}>
        {filtered.map(sub=>{const sel=selected.has(sub.externalId); return (
          <div key={sub.externalId} onClick={()=>toggle(sub.externalId)} style={{position:'relative',borderRadius:'10px',overflow:'hidden',cursor:'pointer',border:sel?'2px solid #ef4444':'2px solid rgba(255,255,255,0.08)',background:sel?'rgba(239,68,68,0.08)':'rgba(255,255,255,0.03)',transition:'all 0.2s'}}>
            <div style={{width:'100%',aspectRatio:'1',background:'var(--bg-surface)',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
              {sub.imageUrl?<img src={oostoProxyUrl(sub.imageUrl)} alt={sub.name} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>{e.target.style.display='none';}} />:<Users size={32} style={{color:'var(--text-tertiary)'}} />}
            </div>
            <div style={{padding:'8px 10px'}}><div style={{fontSize:'12px',fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{sub.name||'Unknown'}</div>{sub.cardNumber&&<div style={{fontSize:'10px',color:'var(--text-tertiary)',marginTop:'2px'}}>Card #{sub.cardNumber}</div>}</div>
            {sel&&<div style={{position:'absolute',top:'6px',right:'6px',width:'24px',height:'24px',borderRadius:'50%',background:'#ef4444',display:'flex',alignItems:'center',justifyContent:'center'}}><Check size={14} color="#fff"/></div>}
          </div>
        );})}
      </div>}
    </div>
  );
}

// ─── PEOPLE TAB ───────────────────────────────────────────────

function PeopleTab() {
  const [persons, setPersons] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const searchTimeout = useRef(null);

  const fetchPersons = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      const res = await fetchWithTenant(`/api/registration/ac-persons?${params}`);
      const data = await res.json();
      setPersons(data.persons || []);
      setTotal(data.total || 0);
      setSource(data.source || null);
    } catch (err) { console.error('Failed to fetch AC persons:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPersons(''); }, [fetchPersons]);

  const handleSearch = (val) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setSearch(val); fetchPersons(val); }, 300);
  };

  return (
    <div>
      <div className="reg-card">
        <div className="reg-toolbar">
          <div style={{position:'relative',flex:'1 1 250px'}}>
            <Search size={16} style={{position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',color:'var(--text-tertiary)'}} />
            <input className="reg-search-input" placeholder="Search by name or card number..." onChange={e => handleSearch(e.target.value)} />
          </div>
          {source && <span style={{fontSize:'12px',color:'var(--text-tertiary)',padding:'6px 12px',background:'rgba(255,255,255,0.04)',borderRadius:'6px'}}>Source: {source.name}</span>}
          <button className="reg-btn reg-btn-ghost reg-btn-sm" onClick={() => fetchPersons(search)}><RefreshCw size={14} /></button>
        </div>
        {loading ? <div className="reg-empty"><Loader2 size={24} style={{animation:'spin 1s linear infinite'}} /></div>
         : persons.length === 0 ? <div className="reg-empty"><Users size={40} style={{marginBottom:'12px',opacity:0.3}} /><p style={{margin:0}}>{source ? 'No people in access control' : 'No access control system configured'}</p></div>
         : <div style={{overflowX:'auto'}}>
            <table className="reg-table"><thead><tr>
              <th className="reg-th">Name</th>
              <th className="reg-th">Card #</th>
              <th className="reg-th">FR Status</th>
              <th className="reg-th">Active</th>
            </tr></thead><tbody>
              {persons.map((p, i) => (
                <tr key={p.externalId || i} style={{cursor:'default',transition:'background 0.15s'}}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <td className="reg-td">
                    <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                      {p.frImageUrl ? (
                        <img src={oostoProxyUrl(p.frImageUrl)} alt="" style={{width:'32px',height:'32px',borderRadius:'50%',objectFit:'cover'}} />
                      ) : (
                        <div style={{width:'32px',height:'32px',borderRadius:'50%',background:'rgba(255,255,255,0.06)',display:'flex',alignItems:'center',justifyContent:'center'}}><Users size={14} style={{color:'var(--text-tertiary)'}} /></div>
                      )}
                      <div><div style={{fontWeight:500}}>{p.name || (p.firstName + ' ' + p.lastName)}</div></div>
                    </div>
                  </td>
                  <td className="reg-td"><span style={{fontFamily:'monospace',fontSize:'13px',color:'var(--text-secondary)'}}>{p.cardNumber || '-'}</span></td>
                  <td className="reg-td">
                    {p.frEnrolled
                      ? <span style={{...statusBadge('ACTIVE'),gap:'4px'}}><CheckCircle size={10} /> Enrolled</span>
                      : <span style={{...statusBadge('DEREGISTERED'),gap:'4px'}}><XCircle size={10} /> Not enrolled</span>
                    }
                  </td>
                  <td className="reg-td">
                    {p.isActive !== false
                      ? <span style={{width:'8px',height:'8px',borderRadius:'50%',background:'#22c55e',display:'inline-block'}} />
                      : <span style={{width:'8px',height:'8px',borderRadius:'50%',background:'#ef4444',display:'inline-block'}} />
                    }
                  </td>
                </tr>
              ))}
            </tbody></table>
            <div className="reg-pagination"><span>{total} person{total !== 1 ? 's' : ''} in access control</span></div>
          </div>
        }
      </div>
      {selectedPerson && <PersonDetail personId={selectedPerson.id} onClose={() => setSelectedPerson(null)} onUpdate={() => fetchPersons(search)} />}
    </div>
  );
}

// ─── PERSON DETAIL ────────────────────────────────────────────

function PersonDetail({ personId, onClose, onUpdate }) {
  const [person, setPerson] = useState(null); const [loading, setLoading] = useState(true); const [al, setAl] = useState('');
  useEffect(()=>{(async()=>{setLoading(true);try{const r=await fetchWithTenant(`/api/registration/persons/${personId}`);setPerson(await r.json());}catch(e){}setLoading(false);})();},[personId]);
  const handleRetry = async()=>{setAl('retry');try{await fetchWithTenant(`/api/registration/persons/${personId}/retry`,{method:'POST'});toast.success('Retried');const r=await fetchWithTenant(`/api/registration/persons/${personId}`);setPerson(await r.json());}catch(e){toast.error(e.message);}setAl('');};

  return (<><div className="reg-overlay" onClick={onClose}/><div className="reg-detail-panel">
    <div className="reg-modal-header" style={{flexShrink:0}}><h3 style={{margin:0,fontSize:'16px'}}>Person Detail</h3><button className="reg-btn reg-btn-ghost" style={{padding:'6px'}} onClick={onClose}><X size={18}/></button></div>
    <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>
      {loading?<div className="reg-empty"><Loader2 size={24} style={{animation:'spin 1s linear infinite'}}/></div>:!person?<div className="reg-empty">Not found</div>:<>
        <div style={{display:'flex',alignItems:'center',gap:'16px',marginBottom:'20px'}}>
          <div style={{width:'56px',height:'56px',borderRadius:'50%',background:person.photoUrl?`url(${person.photoUrl}) center/cover`:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,backgroundSize:'cover'}}>{!person.photoUrl&&<Users size={24} style={{color:'var(--text-tertiary)'}}/>}</div>
          <div><h3 style={{margin:0,fontSize:'18px'}}>{person.firstName} {person.lastName}</h3><div style={{display:'flex',gap:'8px',marginTop:'4px',flexWrap:'wrap'}}><span style={statusBadge(person.status)}>{person.status}</span>{person.department&&<span style={{fontSize:'12px',color:'var(--text-tertiary)'}}>{person.department}</span>}</div></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'20px',fontSize:'13px'}}>
          {person.email&&<div style={{color:'var(--text-secondary)'}}><Mail size={12} style={{marginRight:'4px'}}/>{person.email}</div>}
          {person.phone&&<div style={{color:'var(--text-secondary)'}}><Phone size={12} style={{marginRight:'4px'}}/>{person.phone}</div>}
          {person.accessLevel&&<div style={{color:'var(--text-secondary)'}}><Shield size={12} style={{marginRight:'4px'}}/>{person.accessLevel}</div>}
        </div>
        {person.providerSyncs?.some(sy=>sy.syncStatus==='FAILED'||sy.syncStatus==='RETRY')&&<button className="reg-btn" style={{marginBottom:'20px'}} onClick={handleRetry} disabled={!!al}>{al==='retry'?<Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/>:<RotateCcw size={14}/>} Retry Failed</button>}
        <h4 style={{fontSize:'13px',color:'var(--text-tertiary)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'10px'}}>Provider Sync</h4>
        <div style={{display:'flex',flexDirection:'column',gap:'8px',marginBottom:'24px'}}>
          {(person.providerSyncs||[]).map(sy=><div key={sy.id} className="reg-provider-card"><div><div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'14px',fontWeight:500}}><span style={syncDot(sy.syncStatus)}/>{sy.integration?.name||PROVIDER_LABELS[sy.providerType]}</div><div style={{fontSize:'11px',color:'var(--text-tertiary)',marginTop:'2px'}}>{PROVIDER_LABELS[sy.providerType]}{sy.externalId?` | ${sy.externalId.substring(0,20)}`:''}</div>{sy.lastError&&<div style={{fontSize:'11px',color:'#ef4444',marginTop:'2px'}}>{sy.lastError}</div>}</div><span style={statusBadge(sy.syncStatus==='SYNCED'?'ACTIVE':sy.syncStatus==='FAILED'?'SUSPENDED':'PENDING')}>{sy.syncStatus}</span></div>)}
        </div>
        {person.groups?.length>0&&<><h4 style={{fontSize:'13px',color:'var(--text-tertiary)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'10px'}}>Groups</h4><div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'24px'}}>{person.groups.map(pg=><span key={pg.group.id} style={{padding:'4px 12px',borderRadius:'12px',fontSize:'12px',fontWeight:500,background:'rgba(0,212,170,0.1)',color:'#00d4aa'}}>{pg.group.name}</span>)}</div></>}
        {person.actions?.length>0&&<><h4 style={{fontSize:'13px',color:'var(--text-tertiary)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'10px'}}>Activity</h4>{person.actions.slice(0,15).map(a=><div key={a.id} className="reg-audit-row"><div style={{width:'8px',height:'8px',borderRadius:'50%',background:a.success?'#22c55e':'#ef4444',marginTop:'5px',flexShrink:0}}/><div style={{flex:1}}><div style={{fontWeight:500}}>{ACTION_LABELS[a.actionType]||a.actionType}</div>{a.integration&&<div style={{fontSize:'11px',color:'var(--text-tertiary)'}}>{a.integration.name}</div>}</div><div style={{fontSize:'11px',color:'var(--text-tertiary)',textAlign:'right',whiteSpace:'nowrap'}}>{new Date(a.createdAt).toLocaleString()}</div></div>)}</>}
      </>}
    </div>
  </div></>);
}

// ─── GROUPS TAB ───────────────────────────────────────────────

function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await fetchWithTenant('/api/registration/ac-groups');
      const data = await res.json();
      setGroups(data.groups || []);
      setSource(data.source || null);
    } catch (err) { console.error('Failed to fetch AC groups:', err); }
    setLoading(false);
  };

  useEffect(() => { fetchGroups(); }, []);

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <p style={{margin:0,fontSize:'14px',color:'var(--text-secondary)'}}>{groups.length} access level{groups.length !== 1 ? 's' : ''}</p>
          {source && <span style={{fontSize:'11px',color:'var(--text-tertiary)',padding:'4px 10px',background:'rgba(255,255,255,0.04)',borderRadius:'6px'}}>From: {source.name}</span>}
        </div>
        <button className="reg-btn reg-btn-ghost reg-btn-sm" onClick={fetchGroups}><RefreshCw size={14} /></button>
      </div>
      {loading ? <div className="reg-empty"><Loader2 size={24} style={{animation:'spin 1s linear infinite'}} /></div>
       : groups.length === 0 ? (
        <div className="reg-card reg-empty">
          <FolderOpen size={40} style={{marginBottom:'12px',opacity:0.3}} />
          <p style={{margin:0}}>{source ? 'No access levels found' : 'No access control system configured'}</p>
          <p style={{margin:'4px 0 0',fontSize:'13px',color:'var(--text-tertiary)'}}>Access levels are managed in your access control system</p>
        </div>
       ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
          {groups.map(g => (
            <div key={g.id || g.href} className="reg-group-card">
              <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                <Shield size={16} className="reg-icon-accent" />
                <div>
                  <div style={{fontWeight:500,fontSize:'14px'}}>{g.name}</div>
                  <div style={{fontSize:'11px',color:'var(--text-tertiary)',marginTop:'2px'}}>
                    {g.type === 'access_level' ? 'Access Level' : g.type || 'Group'}
                    {g.href ? ` | ${g.href}` : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
       )}
    </div>
  );
}

// ─── AUDIT TAB ────────────────────────────────────────────────

function AuditTab() {
  const [actions, setActions] = useState([]); const [total, setTotal] = useState(0); const [page, setPage] = useState(1); const [loading, setLoading] = useState(true); const [filter, setFilter] = useState('');
  useEffect(()=>{(async()=>{setLoading(true);try{const p=new URLSearchParams({page,limit:50});if(filter)p.set('actionType',filter);const r=await fetchWithTenant(`/api/registration/audit?${p}`);const d=await r.json();setActions(d.actions||[]);setTotal(d.total||0);}catch(e){}setLoading(false);})();},[page,filter]);

  return (<div className="reg-card">
    <div className="reg-toolbar"><select className="reg-select" value={filter} onChange={e=>{setFilter(e.target.value);setPage(1);}}><option value="">All</option>{Object.entries(ACTION_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select><span style={{fontSize:'13px',color:'var(--text-tertiary)'}}>{total} entries</span></div>
    {loading?<div className="reg-empty"><Loader2 size={24} style={{animation:'spin 1s linear infinite'}}/></div>
     :actions.length===0?<div className="reg-empty"><Activity size={40} style={{marginBottom:'12px',opacity:0.3}}/><p style={{margin:0}}>No entries</p></div>
     :<>{actions.map(a=><div key={a.id} className="reg-audit-row"><div style={{width:'8px',height:'8px',borderRadius:'50%',background:a.success?'#22c55e':'#ef4444',marginTop:'5px',flexShrink:0}}/><div style={{flex:1,minWidth:0}}><div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}><span style={{fontWeight:500}}>{ACTION_LABELS[a.actionType]||a.actionType}</span>{a.person&&<span style={{color:'var(--text-secondary)'}}>{a.person.firstName} {a.person.lastName}</span>}</div>{a.integration&&<div style={{fontSize:'11px',color:'var(--text-tertiary)',marginTop:'2px'}}>{a.integration.name}</div>}</div><div style={{fontSize:'11px',color:'var(--text-tertiary)',textAlign:'right',flexShrink:0,whiteSpace:'nowrap'}}>{new Date(a.createdAt).toLocaleString()}{a.actionBy&&<div>{a.actionBy.name}</div>}</div></div>)}
      <div className="reg-pagination"><span>{total} total</span><div style={{display:'flex',gap:'8px'}}><button className="reg-btn reg-btn-ghost reg-btn-sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}><ChevronLeft size={14}/></button><button className="reg-btn reg-btn-ghost reg-btn-sm" disabled={actions.length<50} onClick={()=>setPage(p=>p+1)}><ChevronRight size={14}/></button></div></div>
    </>}
  </div>);
}
